import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil, tap } from 'rxjs';
import { clearAllState } from '../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { LAST_SELECTED_LETTER_ID_STORAGE_KEY } from '../../letters.constants';
import { PatientLettersService } from '../../services/letters.service';
import * as actions from '../actions/letters.actions';

@Injectable()
export class LetterEffects {
  private actions$ = inject(Actions);
  private patientLettersService = inject(PatientLettersService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientLetters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientLetters),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) => {
        return this.patientLettersService.getPatientLetters(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(letters => {
            return actions.getPatientLettersSuccess({ letters });
          }),
          catchError(err => {
            return of(actions.getPatientLettersFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  getLetterById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientLetter),
      concatLatestFrom(() => this.store.select(selectPatientId)),
      mergeMap(([action, patientId]) => {
        return this.patientLettersService.getPatientLetter(action.letterId, patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(letter => {
            return actions.getPatientLetterSuccess({ letter });
          }),
          catchError(err => {
            return of(actions.getPatientLetterFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  // A patient switch can originate from a letter-detail route where LettersComponent is not mounted,
  // so clear the remembered letter id centrally rather than relying on the component's own cleanup.
  // Also purge on clearAllState (logout / account switch) so the id never outlives the session that
  // owns it — matching cancelRequest$, which cancels in-flight requests on both actions.
  clearSelectedLetterOnPatientSwitch$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(patientSwitchInitiated, clearAllState),
        tap(() => sessionStorage.removeItem(LAST_SELECTED_LETTER_ID_STORAGE_KEY))
      );
    },
    { dispatch: false }
  );

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.recordPatientLetterPrintEvent),
      mergeMap(({ patientId, patientLetterId }) =>
        this.patientLettersService.postPrintEvent(patientId, patientLetterId).pipe(
          map(() => actions.recordPatientLetterPrintEventSuccess()),
          catchError(err => of(actions.recordPatientLetterPrintEventFailure({ error: err })))
        )
      )
    );
  });
}
